import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  FaBell, 
  FaBullhorn, 
  FaLaptopCode, 
  FaUsersCog, 
  FaRegLightbulb, 
  FaChartLine, 
  FaUsers, 
  FaFolder, 
  FaCog,
  FaTags,
  FaLink,
  FaSync,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle
} from 'react-icons/fa';
import useAuthenticatedAxios from '../auth/AuthenticatedAxios';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SpinningIcon = styled(FaSync)`
  animation: ${props => props.$spinning ? `${spin} 1s linear infinite` : 'none'};
`;

const AdminPanelContainer = styled.div`
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
  color: #ffffff;
  overflow-y: auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 15px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #ffffff;
`;

const TabNav = styled.div`
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const TabButton = styled.button`
  background-color: ${props => props.$active ? 'rgba(53, 124, 210, 0.2)' : 'transparent'};
  color: ${props => props.$active ? '#357CD2' : '#ffffff'};
  border: none;
  padding: 12px 20px;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: ${props => props.$active ? '#357CD2' : 'transparent'};
  }
  
  &:hover {
    background-color: ${props => props.$active ? 'rgba(53, 124, 210, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
  }
`;

const Section = styled.div`
  background-color: #2D2D2D;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const SectionContent = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 500;
  margin-bottom: 20px;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const NotificationCard = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 20px;
  margin-bottom: 15px;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  background-color: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: white;
  font-size: 16px;

  &:focus {
    border-color: #357CD2;
    outline: none;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  background-color: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: white;
  font-size: 16px;
  min-height: 100px;
  resize: vertical;

  &:focus {
    border-color: #357CD2;
    outline: none;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  background-color: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: white;
  font-size: 16px;

  &:focus {
    border-color: #357CD2;
    outline: none;
  }
`;

const Button = styled.button`
  background-color: #357CD2;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #2a63a8;
  }

  &:disabled {
    background-color: #2a4a72;
    cursor: not-allowed;
  }
`;

const NotificationPreview = styled.div`
  background-color: #2D2D2D;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 15px;
  margin-top: 20px;
  display: ${props => props.$visible ? 'flex' : 'none'};
  gap: 12px;
`;

const IconContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => 
    props.$type === 'feature' ? 'rgba(53, 124, 210, 0.2)' : 
    props.$type === 'tip' ? 'rgba(92, 184, 92, 0.2)' : 
    props.$type === 'update' ? 'rgba(240, 173, 78, 0.2)' : 
    'rgba(91, 192, 222, 0.2)'
  };
  color: ${props => 
    props.$type === 'feature' ? '#357CD2' : 
    props.$type === 'tip' ? '#5cb85c' : 
    props.$type === 'update' ? '#f0ad4e' : 
    '#5bc0de'
  };
`;

const ContentContainer = styled.div`
  flex: 1;
`;

const PreviewTitle = styled.div`
  font-weight: 500;
  margin-bottom: 5px;
`;

const PreviewDescription = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 5px;
`;

const PreviewDate = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
`;

const SuccessMessage = styled.div`
  background-color: rgba(92, 184, 92, 0.2);
  color: #5cb85c;
  padding: 15px;
  border-radius: 4px;
  margin-top: 20px;
  display: ${props => props.$visible ? 'block' : 'none'};
`;

const PlaceholderMessage = styled.div`
  padding: 40px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  
  h3 {
    font-size: 18px;
    margin-bottom: 10px;
  }
  
  p {
    font-size: 14px;
  }
`;

const UserTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  flex: 1;
  
  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  th {
    color: rgba(255, 255, 255, 0.7);
    font-weight: 500;
  }
  
  td {
    color: rgba(255, 255, 255, 0.9);
  }
  
  tr:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const UserActions = styled.div`
  display: flex;
  gap: 8px;
  
  button {
    background-color: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: white;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 12px;
    cursor: pointer;
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
    
    &.edit {
      color: #5bc0de;
      border-color: rgba(91, 192, 222, 0.3);
    }
    
    &.delete {
      color: #d9534f;
      border-color: rgba(217, 83, 79, 0.3);
    }
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const FormContent = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const FormActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

/**
 * AdminPanel Component
 * 
 * Administrative dashboard for authorized users to manage platform-wide settings and send notifications
 */
const AdminPanel = () => {
  const { user, getAccessTokenSilently } = useAuth0();
  const axios = useAuthenticatedAxios();
  const [activeTab, setActiveTab] = useState('notifications');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('feature');
  const [showPreview, setShowPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // IRCC URL Health state
  const [irccHealth, setIrccHealth] = useState({ health: [], summary: { total: 0, valid: 0, invalid: 0, unknown: 0 } });
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [validatingUrls, setValidatingUrls] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  
  // Load IRCC URL health
  const loadIrccHealth = useCallback(async () => {
    if (!axios) return;
    setLoadingHealth(true);
    try {
      const response = await axios.get('/document-templates/ircc-url-health');
      if (response.data.success) {
        setIrccHealth(response.data);
      }
    } catch (error) {
      console.error('Error loading IRCC health:', error);
    } finally {
      setLoadingHealth(false);
    }
  }, [axios]);
  
  // Trigger URL validation
  const triggerValidation = async () => {
    if (!axios) return;
    setValidatingUrls(true);
    setValidationResult(null);
    try {
      const response = await axios.post('/document-templates/ircc-url-health/validate');
      if (response.data.success) {
        setValidationResult(response.data.report);
        loadIrccHealth(); // Refresh the list
      }
    } catch (error) {
      console.error('Error validating URLs:', error);
      setValidationResult({ error: error.message });
    } finally {
      setValidatingUrls(false);
    }
  };
  
  // Load health when IRCC tab is active
  useEffect(() => {
    if (activeTab === 'ircc-health') {
      loadIrccHealth();
    }
  }, [activeTab, loadIrccHealth]);

  // Mock data for users tab
  const mockUsers = [
    { id: 1, name: 'John Doe', email: 'john@example.com', role: 'User', status: 'Active' },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com', role: 'Admin', status: 'Active' },
    { id: 3, name: 'Bob Johnson', email: 'bob@example.com', role: 'Adviser', status: 'Pending' },
    { id: 4, name: 'Alice Brown', email: 'alice@example.com', role: 'User', status: 'Inactive' }
  ];

  // Get the appropriate icon based on notification type
  const getIconComponent = (type) => {
    switch(type) {
      case 'feature':
        return <FaLaptopCode />;
      case 'tip':
        return <FaBullhorn />;
      case 'update':
        return <FaUsersCog />;
      case 'feedback':
        return <FaRegLightbulb />;
      default:
        return <FaChartLine />;
    }
  };

  // Toggle preview
  const handlePreview = () => {
    setShowPreview(!showPreview);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Create notification object
      const notification = {
        type,
        title,
        description,
        icon: type // The backend will map this to the appropriate icon
      };
      
      // Get auth token for API request
      const token = await getAccessTokenSilently();
      
      // In a real implementation, this would send the notification to the backend
      // via API call, which would then broadcast to all users via WebSockets
      
      /* 
      // Real API call that would be used in production
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER}api/notifications`, 
        notification,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        setSuccess(true);
        
        // Reset form after success
        setTimeout(() => {
          setTitle('');
          setDescription('');
          setType('feature');
          setShowPreview(false);
          setSuccess(false);
        }, 3000);
      }
      */
      
      // Simulating API call with setTimeout for demo purposes
      // Remove this in production and use the real API call above
      setTimeout(() => {
        setSubmitting(false);
        setSuccess(true);
        
        // Reset form after success message shows
        setTimeout(() => {
          setTitle('');
          setDescription('');
          setType('feature');
          setShowPreview(false);
          setSuccess(false);
        }, 3000);
      }, 1000);
      
    } catch (error) {
      console.error('Error sending notification:', error);
      setSubmitting(false);
      // Here you would handle the error, perhaps showing an error message
    }
  };

  // Render the appropriate tab content
  const renderTabContent = () => {
    switch(activeTab) {
      case 'notifications':
        return (
          <Section>
            <SectionTitle><FaBell /> Platform Notifications</SectionTitle>
            <p style={{ marginBottom: '20px' }}>
              Create and send notifications to all platform users. These will appear in the platform notifications panel.
            </p>
            
            <SectionContent>
              <NotificationCard>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <FormContainer>
                    <FormContent>
                      <FormGroup>
                        <Label htmlFor="notification-type">Notification Type</Label>
                        <Select 
                          id="notification-type" 
                          value={type} 
                          onChange={(e) => setType(e.target.value)}
                          required
                        >
                          <option value="feature">Feature Announcement</option>
                          <option value="tip">Tip / Advice</option>
                          <option value="update">System Update</option>
                          <option value="feedback">Feedback Request</option>
                        </Select>
                      </FormGroup>
                      
                      <FormGroup>
                        <Label htmlFor="notification-title">Title</Label>
                        <Input 
                          id="notification-title" 
                          value={title} 
                          onChange={(e) => setTitle(e.target.value)}
                          placeholder="Enter notification title"
                          required
                        />
                      </FormGroup>
                      
                      <FormGroup>
                        <Label htmlFor="notification-description">Description</Label>
                        <TextArea 
                          id="notification-description" 
                          value={description} 
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="Enter notification description"
                          required
                        />
                      </FormGroup>
                    </FormContent>
                    
                    <FormActions>
                      <Button type="button" onClick={handlePreview} style={{ backgroundColor: '#4a4a4a' }}>
                        {showPreview ? 'Hide Preview' : 'Show Preview'}
                      </Button>
                      <Button type="submit" disabled={submitting}>
                        {submitting ? 'Sending...' : 'Send Notification'}
                      </Button>
                    </FormActions>
                    
                    <NotificationPreview $visible={showPreview}>
                      <IconContainer $type={type}>
                        {getIconComponent(type)}
                      </IconContainer>
                      <ContentContainer>
                        <PreviewTitle>{title || 'Notification Title'}</PreviewTitle>
                        <PreviewDescription>{description || 'Notification description will appear here.'}</PreviewDescription>
                        <PreviewDate>Just now</PreviewDate>
                      </ContentContainer>
                    </NotificationPreview>
                    
                    <SuccessMessage $visible={success}>
                      Notification sent successfully to all users!
                    </SuccessMessage>
                  </FormContainer>
                </form>
              </NotificationCard>
            </SectionContent>
          </Section>
        );
      
      case 'users':
        return (
          <Section>
            <SectionTitle><FaUsers /> Manage Users & Advisers</SectionTitle>
            <p style={{ marginBottom: '20px' }}>
              View, edit, and manage user accounts and adviser profiles. Control access and permissions.
            </p>
            
            <SectionContent>
              <NotificationCard>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <Input 
                    placeholder="Search users..." 
                    style={{ maxWidth: '300px' }}
                  />
                  <Button>Add New User</Button>
                </div>
                
                <UserTable>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mockUsers.map(user => (
                      <tr key={user.id}>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>{user.role}</td>
                        <td>{user.status}</td>
                        <td>
                          <UserActions>
                            <button className="edit">Edit</button>
                            <button className="delete">Delete</button>
                          </UserActions>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </UserTable>
              </NotificationCard>
            </SectionContent>
          </Section>
        );
      
      case 'categories':
        return (
          <Section>
            <SectionTitle><FaTags /> Manage Categories</SectionTitle>
            <p style={{ marginBottom: '20px' }}>
              Create, edit, and organize platform categories. Categories are used to organize content and services.
            </p>
            
            <SectionContent>
              <NotificationCard>
                <PlaceholderMessage>
                  <h3>Category Management</h3>
                  <p>This section will allow administrators to manage platform categories.</p>
                  <Button style={{ marginTop: '20px' }}>Create New Category</Button>
                </PlaceholderMessage>
              </NotificationCard>
            </SectionContent>
          </Section>
        );
      
      case 'settings':
        return (
          <Section>
            <SectionTitle><FaCog /> Platform Settings</SectionTitle>
            <p style={{ marginBottom: '20px' }}>
              Configure global platform settings, integrations, and system preferences.
            </p>
            
            <SectionContent>
              <NotificationCard>
                <PlaceholderMessage>
                  <h3>Platform Settings</h3>
                  <p>This section will contain global platform configuration options.</p>
                </PlaceholderMessage>
              </NotificationCard>
            </SectionContent>
          </Section>
        );
      
      case 'ircc-health':
        return (
          <Section>
            <SectionTitle><FaLink /> IRCC Form URL Health</SectionTitle>
            <p style={{ marginBottom: '20px' }}>
              Monitor the health of IRCC form download URLs. Broken URLs prevent experts from importing forms.
            </p>
            
            <SectionContent>
              {/* Summary Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px' }}>
                <div style={{ 
                  background: 'rgba(255, 255, 255, 0.05)', 
                  borderRadius: '8px', 
                  padding: '20px', 
                  textAlign: 'center',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#357CD2' }}>{irccHealth.summary?.total || 0}</div>
                  <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', marginTop: '4px' }}>Total Forms</div>
                </div>
                <div style={{ 
                  background: 'rgba(16, 185, 129, 0.1)', 
                  borderRadius: '8px', 
                  padding: '20px', 
                  textAlign: 'center',
                  border: '1px solid rgba(16, 185, 129, 0.3)'
                }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#10b981' }}>{irccHealth.summary?.valid || 0}</div>
                  <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', marginTop: '4px' }}>Valid URLs</div>
                </div>
                <div style={{ 
                  background: irccHealth.summary?.invalid > 0 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255, 255, 255, 0.05)', 
                  borderRadius: '8px', 
                  padding: '20px', 
                  textAlign: 'center',
                  border: `1px solid ${irccHealth.summary?.invalid > 0 ? 'rgba(239, 68, 68, 0.3)' : 'rgba(255, 255, 255, 0.1)'}`
                }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: irccHealth.summary?.invalid > 0 ? '#ef4444' : 'rgba(255,255,255,0.5)' }}>
                    {irccHealth.summary?.invalid || 0}
                  </div>
                  <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', marginTop: '4px' }}>Broken URLs</div>
                </div>
                <div style={{ 
                  background: 'rgba(255, 255, 255, 0.05)', 
                  borderRadius: '8px', 
                  padding: '20px', 
                  textAlign: 'center',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'rgba(255,255,255,0.5)' }}>{irccHealth.summary?.unknown || 0}</div>
                  <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', marginTop: '4px' }}>Not Checked</div>
                </div>
              </div>
              
              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                <Button 
                  onClick={triggerValidation} 
                  disabled={validatingUrls}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <SpinningIcon $spinning={validatingUrls} />
                  {validatingUrls ? 'Validating...' : 'Validate All URLs Now'}
                </Button>
                <Button 
                  onClick={loadIrccHealth} 
                  disabled={loadingHealth}
                  style={{ backgroundColor: '#4a4a4a', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <SpinningIcon $spinning={loadingHealth} />
                  Refresh
                </Button>
              </div>
              
              {/* Validation Result */}
              {validationResult && !validationResult.error && (
                <div style={{ 
                  background: validationResult.invalid > 0 || validationResult.contentChangedCount > 0 
                    ? 'rgba(239, 68, 68, 0.1)' 
                    : 'rgba(16, 185, 129, 0.1)',
                  border: `1px solid ${validationResult.invalid > 0 || validationResult.contentChangedCount > 0 
                    ? 'rgba(239, 68, 68, 0.3)' 
                    : 'rgba(16, 185, 129, 0.3)'}`,
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                    {validationResult.invalid > 0 || validationResult.contentChangedCount > 0 
                      ? '⚠️ Sync Complete - Action Required' 
                      : '✅ Sync Complete - All Good'}
                  </div>
                  <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>
                    Checked {validationResult.totalChecked} forms: {validationResult.valid} valid, {validationResult.invalid} invalid
                  </div>
                  {validationResult.newlyBroken?.length > 0 && (
                    <div style={{ marginTop: '8px', color: '#ef4444', fontSize: '14px' }}>
                      🚨 {validationResult.newlyBroken.length} URL(s) newly broken!
                    </div>
                  )}
                  {validationResult.contentChangedCount > 0 && (
                    <div style={{ marginTop: '8px', color: '#f59e0b', fontSize: '14px' }}>
                      📝 {validationResult.contentChangedCount} document(s) updated by IRCC! Experts have been notified.
                    </div>
                  )}
                  {validationResult.contentChanged?.length > 0 && (
                    <div style={{ marginTop: '12px', fontSize: '13px' }}>
                      <div style={{ fontWeight: 500, marginBottom: '6px' }}>Updated forms:</div>
                      {validationResult.contentChanged.map(c => (
                        <div key={c.formId} style={{ marginLeft: '12px', color: 'rgba(255,255,255,0.7)' }}>
                          • {c.formCode}: v{c.oldVersion} → v{c.newVersion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* URL Health Table */}
              <NotificationCard style={{ padding: 0, overflow: 'hidden' }}>
                {loadingHealth ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                    Loading URL health status...
                  </div>
                ) : irccHealth.health?.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                    No IRCC forms found. Click "Validate All URLs Now" to check URLs.
                  </div>
                ) : (
                  <UserTable style={{ margin: 0 }}>
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}>URL</th>
                        <th style={{ width: '40px' }}>Local</th>
                        <th>Form Code</th>
                        <th>Version</th>
                        <th>Last Sync</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {irccHealth.health?.map(h => (
                        <tr key={h.formId} style={{ 
                          background: h.status === 'invalid' ? 'rgba(239, 68, 68, 0.05)' : undefined 
                        }}>
                          <td style={{ textAlign: 'center' }}>
                            {h.status === 'valid' && <FaCheckCircle style={{ color: '#10b981' }} title="URL Valid" />}
                            {h.status === 'invalid' && <FaTimesCircle style={{ color: '#ef4444' }} title="URL Broken" />}
                            {h.status === 'unknown' && <FaExclamationTriangle style={{ color: '#f59e0b' }} title="Not Checked" />}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {h.hasLocalCopy 
                              ? <FaCheckCircle style={{ color: '#10b981' }} title="PDF Stored Locally" />
                              : <FaTimesCircle style={{ color: 'rgba(255,255,255,0.3)' }} title="No Local Copy" />
                            }
                          </td>
                          <td>
                            <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>{h.formCode}</div>
                            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>{h.formName}</div>
                          </td>
                          <td style={{ fontSize: '13px' }}>
                            <span style={{ 
                              background: 'rgba(99, 102, 241, 0.2)', 
                              padding: '2px 8px', 
                              borderRadius: '4px',
                              fontSize: '12px' 
                            }}>
                              v{h.version || 1}
                            </span>
                            {h.fileSize && (
                              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>
                                {(h.fileSize / 1024).toFixed(0)} KB
                              </div>
                            )}
                          </td>
                          <td style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                            {h.lastChecked ? new Date(h.lastChecked).toLocaleString() : 'Never'}
                            {h.lastContentChange && (
                              <div style={{ fontSize: '10px', color: '#f59e0b', marginTop: '2px' }}>
                                Updated: {new Date(h.lastContentChange).toLocaleDateString()}
                              </div>
                            )}
                          </td>
                          <td style={{ fontSize: '12px', maxWidth: '150px' }}>
                            {h.status === 'invalid' 
                              ? <span style={{ color: '#ef4444' }}>{h.errorMessage || `HTTP ${h.statusCode}`}</span>
                              : h.hasLocalCopy 
                                ? <span style={{ color: '#10b981' }}>Ready</span>
                                : <span style={{ color: '#f59e0b' }}>Needs Sync</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </UserTable>
                )}
              </NotificationCard>
              
              {/* Help Text */}
              <div style={{ marginTop: '20px', padding: '16px', background: 'rgba(99, 102, 241, 0.1)', borderRadius: '8px', border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                <div style={{ fontWeight: 600, marginBottom: '8px', color: '#a5b4fc' }}>💡 How to Fix Broken URLs</div>
                <ol style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                  <li>Go to IRCC website and find the form's new download URL</li>
                  <li>Update the URL in <code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 6px', borderRadius: '4px' }}>src/scripts/updateIRCCUrls.ts</code></li>
                  <li>Run <code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 6px', borderRadius: '4px' }}>npx ts-node src/scripts/updateIRCCUrls.ts</code> to update database</li>
                </ol>
              </div>
            </SectionContent>
          </Section>
        );
      
      default:
        return (
          <Section>
            <SectionContent>
              <PlaceholderMessage>
                <h3>Select a tab to continue</h3>
                <p>Choose one of the management tabs above to access administrative functions.</p>
              </PlaceholderMessage>
            </SectionContent>
          </Section>
        );
    }
  };

  return (
    <AdminPanelContainer>
      <Header>
        <Title>Admin Panel</Title>
      </Header>

      <TabNav>
        <TabButton 
          $active={activeTab === 'notifications'} 
          onClick={() => setActiveTab('notifications')}
        >
          <FaBell /> Notifications
        </TabButton>
        <TabButton 
          $active={activeTab === 'users'} 
          onClick={() => setActiveTab('users')}
        >
          <FaUsers /> Users & Advisers
        </TabButton>
        <TabButton 
          $active={activeTab === 'categories'} 
          onClick={() => setActiveTab('categories')}
        >
          <FaTags /> Categories
        </TabButton>
        <TabButton 
          $active={activeTab === 'settings'} 
          onClick={() => setActiveTab('settings')}
        >
          <FaCog /> Settings
        </TabButton>
        <TabButton 
          $active={activeTab === 'ircc-health'} 
          onClick={() => setActiveTab('ircc-health')}
          style={{ 
            color: irccHealth.summary?.invalid > 0 ? '#ef4444' : undefined,
            background: irccHealth.summary?.invalid > 0 && activeTab !== 'ircc-health' ? 'rgba(239, 68, 68, 0.1)' : undefined 
          }}
        >
          <FaLink /> IRCC Health {irccHealth.summary?.invalid > 0 && `(${irccHealth.summary.invalid})`}
        </TabButton>
      </TabNav>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {renderTabContent()}
      </div>
    </AdminPanelContainer>
  );
};

export default AdminPanel; 